// Code generated by protoc-gen-ts_proto. DO NOT EDIT.
// versions:
//   protoc-gen-ts_proto  v2.11.2
//   protoc               v5.26.1
// source: atk/activities.proto

/* eslint-disable */
import { BinaryReader, BinaryWriter } from "@bufbuild/protobuf/wire";
import { Duration } from "../google/protobuf/duration";
import {
  AutonomousActivityType,
  autonomousActivityTypeFromJSON,
  autonomousActivityTypeToJSON,
  HitchType,
  hitchTypeFromJSON,
  hitchTypeToJSON,
  PtoType,
  ptoTypeFromJSON,
  ptoTypeToJSON,
} from "./enums";

export const protobufPackage = "carbon.atk";

/**
 * Per-rig, per-activity path kinematics, calibrated by the customer before the
 * rig enters service. Stored at the rig level (ImplementControlSchema) and
 * overridable per job.
 */
export interface RigActivityConfig {
  activity: AutonomousActivityType;
  constraints:
    | RigActivityConstraints
    | undefined;
  /**
   * Actuations performed once when entering the activity, as a set of
   * sequential stages.
   */
  quickActionStages: QuickActionStage[];
  /**
   * The transition duration the path planner reserves field space for
   * (transition_speed_mps * quick_action_time < reserved space <=
   * working_speed_mps * quick_action_time). Authoritative for
   * path planning even though individual actions may carry their own durations.
   * Some quick actions carry over into the work task: this time only accounts
   * for the time required to get to the point where the work task can begin.
   */
  quickActionTime:
    | Duration
    | undefined;
  /**
   * ongoing actions have been deprecated in favor of marking some quick actions
   * as carrying over into the work task.
   *
   * @deprecated
   */
  ongoingActions: OngoingAction[];
}

export interface RigActivityConstraints {
  /**
   * The maximum hitch angle is used when planning paths for drawbar implements
   * to prevent the tractor and implement from jackknifing.
   */
  maxHitchAngleRad: number;
  /**
   * The minimum turn radius is used when planning paths for all implements to
   * prevent the tractor from applying excessive side pressure to the implement.
   * Optional at the request of path planning, to allow us to distinguish a
   * default/not-present value from an explicitly specified zero.
   */
  minTurnRadiusM?:
    | number
    | undefined;
  /**
   * ideal speed -- the optimal speed that this activity should be performed
   * at.
   */
  workingSpeedMps: number;
  /**
   * turning speed -- for objectives which have both straight-line and curved
   * portions, this is a second, lower, ideal speed at which the curved portions
   * of the objective are run.
   */
  turningSpeedMps: number;
  /**
   * minimum speed -- the slowest allowable speed that this activity can be
   * performed at.  may be zero, for implements that must be raised and lowered
   * at a full stop.
   */
  transitionSpeedMps: number;
}

export interface RigActivitiesConfiguration {
  activities: RigActivityConfig[];
}

export interface QuickAction {
  /** a unique ID for this action to be used for diagnostics */
  actionId: string;
  scvDetentUp?: ScvDetentUp | undefined;
  scvDetentDown?: ScvDetentDown | undefined;
  scvTimedForce?: ScvTimedForce | undefined;
  scvConstantForce?: ScvConstantForce | undefined;
  scvMoveToPosition?: ScvMoveToPosition | undefined;
  scvFloat?: ScvFloat | undefined;
  ptoOn?:
    | PtoOn
    | undefined;
  /**
   * this action is deprecated because it should not be necessary to turn the
   * PTO off during a quick action stage now that PTO activation doesn't carry
   * over between activities and defaults to off
   *
   * @deprecated
   */
  ptoOff?:
    | PtoOff
    | undefined;
  /**
   * hitch detent up was removed from M6 because the time it takes is
   * unpredictable to the robot and not explicitly configured by the user; it
   * may be reintroduced at some point but there's currently no way to create
   * them in the frontend
   *
   * @deprecated
   */
  hitchDetentUp?:
    | HitchDetentUp
    | undefined;
  /**
   * hitch detent down was removed from M6 because the time it takes is
   * unpredictable to the robot and not explicitly configured by the user; it
   * may be reintroduced at some point but there's currently no way to create
   * them in the frontend
   *
   * @deprecated
   */
  hitchDetentDown?: HitchDetentDown | undefined;
  hitchTimedForce?: HitchTimedForce | undefined;
  hitchSetPosition?: HitchHoldPosition | undefined;
  hitchFloat?: HitchFloat | undefined;
  laserweederOn?: LaserweederOn | undefined;
  laserweederEstopOn?: LaserWeederEstopOn | undefined;
  laserweederEstopOff?:
    | LaserWeederEstopOff
    | undefined;
  /**
   * the standalone wait action is not planned to be supported in M6, at UX
   * request
   *
   * @deprecated
   */
  wait?: Wait | undefined;
  setEngineRpmToMax?: SetEngineRpmToMax | undefined;
  setEngineRpmToMin?: SetEngineRpmToMin | undefined;
  advanced?: AdvancedAction | undefined;
  startDelay: Duration | undefined;
  carryOverIntoWorkTask: boolean;
}

export interface QuickActionStage {
  actions: QuickAction[];
}

/**
 * ongoing actions are now deprecated to simplify the logic required to build
 * an action configuration.  use a quick action that carries over into the work
 * task instead.
 *
 * @deprecated
 */
export interface OngoingAction {
  /** a unique ID for this action to be used for diagnostics */
  actionId: string;
  scvConstantForce?: ScvConstantForce | undefined;
  scvHoldPosition?: ScvHoldPosition | undefined;
  scvFloat?: ScvFloat | undefined;
  ptoOn?: PtoOn | undefined;
  hitchHoldPosition?: HitchHoldPosition | undefined;
  hitchFloat?: HitchFloat | undefined;
  laserweederEstopOn?: LaserWeederEstopOn | undefined;
  laserweederEstopOff?: LaserWeederEstopOff | undefined;
  laserweederOn?: LaserweederOn | undefined;
  advanced?: AdvancedAction | undefined;
}

/**
 * --- SCV input transition actions ---
 *
 * Note: SCV detent up/down cannot be guaranteed by the ATK stack to run the
 * implement to a hard stop, since the detent duration is configured on the
 * tractor VT outside this system. For predictable behavior, prefer
 * ScvTimedForce (known duration) when an action must reliably follow a move.
 */
export interface ScvDetentUp {
  scvInputId: string;
  /**
   * the execution time of a detent up/down action cannot be detected by the
   * ATK system; it must be transcribed from the VT by the customer to estimate
   * proper execution timing.
   */
  vtDetentTime: Duration | undefined;
}

export interface ScvDetentDown {
  scvInputId: string;
  /**
   * the execution time of a detent up/down action cannot be detected by the
   * ATK system; it must be transcribed from the VT by the customer to estimate
   * proper execution timing.
   */
  vtDetentTime: Duration | undefined;
}

export interface ScvTimedForce {
  scvInputId: string;
  /** [-100, 100]; the sign encodes direction. */
  forcePct: number;
  duration: Duration | undefined;
}

export interface ScvMoveToPosition {
  scvInputId: string;
  /** 0.0-1.0 of full extension */
  position: number;
}

/** --- PTO transition actions --- */
export interface PtoOn {
  ptoType: PtoType;
}

export interface PtoOff {
  ptoType: PtoType;
}

/** --- three-point hitch transition actions --- */
export interface HitchDetentUp {
  hitchType: HitchType;
  /**
   * hitch detent actions don't have a known execution time up front, nor do
   * they have a time set by the customer on the VT; as part of initial setup
   * in the companion app, the execution time must be calibrated with a test
   * action to enable sequencing to work properly
   */
  calibratedDuration: Duration | undefined;
}

export interface HitchDetentDown {
  hitchType: HitchType;
  /**
   * hitch detent actions don't have a known execution time up front, nor do
   * they have a time set by the customer on the VT; as part of initial setup
   * in the companion app, the execution time must be calibrated with a test
   * action to enable sequencing to work properly
   */
  calibratedDuration: Duration | undefined;
}

/** force_pct in [-100, 100]; the sign encodes direction. */
export interface HitchTimedForce {
  hitchType: HitchType;
  forcePct: number;
  duration: Duration | undefined;
}

/** --- LaserWeeder transition actions --- */
export interface LaserWeederEstopOn {
  implementId: string;
}

export interface LaserWeederEstopOff {
  implementId: string;
}

/** --- global transition actions (no required effector) --- */
export interface Wait {
  duration: Duration | undefined;
}

/**
 * Sets engine RPM to the maximum configured on the tractor VT. Fails autonomy
 * and raises an intervention if the in-cab controls prevent changing the RPM.
 */
export interface SetEngineRpmToMax {
}

/**
 * Sets engine RPM to the minimum configured on the tractor VT. Fails autonomy
 * and raises an intervention if the in-cab controls prevent changing the RPM.
 */
export interface SetEngineRpmToMin {
}

/**
 * Escape hatch for Carbon engineering to enable customers when no existing
 * action fits. action_name is chosen from a fixed list known to the robot and
 * is restricted to [A-Za-z0-9_].
 */
export interface AdvancedAction {
  actionName: string;
  intParam: number;
  floatParam: number;
}

/** --- SCV input ongoing actions --- */
export interface ScvConstantForce {
  scvInputId: string;
  /** [-100, 100]; sign = direction */
  forcePct: number;
}

/** @deprecated */
export interface ScvHoldPosition {
  scvInputId: string;
  /** 0.0-1.0; needs position sensor */
  position: number;
}

export interface ScvFloat {
  scvInputId: string;
}

/** --- three-point hitch ongoing actions --- */
export interface HitchHoldPosition {
  hitchType: HitchType;
  /** 0.0-1.0 of full height */
  position: number;
}

export interface HitchFloat {
  hitchType: HitchType;
}

/** --- LaserWeeder ongoing actions --- */
export interface LaserweederOn {
  implementId: string;
}

function createBaseRigActivityConfig(): RigActivityConfig {
  return { activity: 0, constraints: undefined, quickActionStages: [], quickActionTime: undefined, ongoingActions: [] };
}

export const RigActivityConfig: MessageFns<RigActivityConfig> = {
  encode(message: RigActivityConfig, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.activity !== 0) {
      writer.uint32(8).int32(message.activity);
    }
    if (message.constraints !== undefined) {
      RigActivityConstraints.encode(message.constraints, writer.uint32(18).fork()).join();
    }
    for (const v of message.quickActionStages) {
      QuickActionStage.encode(v!, writer.uint32(26).fork()).join();
    }
    if (message.quickActionTime !== undefined) {
      Duration.encode(message.quickActionTime, writer.uint32(34).fork()).join();
    }
    for (const v of message.ongoingActions) {
      OngoingAction.encode(v!, writer.uint32(42).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): RigActivityConfig {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseRigActivityConfig();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 8) {
            break;
          }

          message.activity = reader.int32() as any;
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.constraints = RigActivityConstraints.decode(reader, reader.uint32());
          continue;
        }
        case 3: {
          if (tag !== 26) {
            break;
          }

          message.quickActionStages.push(QuickActionStage.decode(reader, reader.uint32()));
          continue;
        }
        case 4: {
          if (tag !== 34) {
            break;
          }

          message.quickActionTime = Duration.decode(reader, reader.uint32());
          continue;
        }
        case 5: {
          if (tag !== 42) {
            break;
          }

          message.ongoingActions.push(OngoingAction.decode(reader, reader.uint32()));
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): RigActivityConfig {
    return {
      activity: isSet(object.activity) ? autonomousActivityTypeFromJSON(object.activity) : 0,
      constraints: isSet(object.constraints) ? RigActivityConstraints.fromJSON(object.constraints) : undefined,
      quickActionStages: globalThis.Array.isArray(object?.quickActionStages)
        ? object.quickActionStages.map((e: any) => QuickActionStage.fromJSON(e))
        : globalThis.Array.isArray(object?.quick_action_stages)
        ? object.quick_action_stages.map((e: any) => QuickActionStage.fromJSON(e))
        : [],
      quickActionTime: isSet(object.quickActionTime)
        ? Duration.fromJSON(object.quickActionTime)
        : isSet(object.quick_action_time)
        ? Duration.fromJSON(object.quick_action_time)
        : undefined,
      ongoingActions: globalThis.Array.isArray(object?.ongoingActions)
        ? object.ongoingActions.map((e: any) => OngoingAction.fromJSON(e))
        : globalThis.Array.isArray(object?.ongoing_actions)
        ? object.ongoing_actions.map((e: any) => OngoingAction.fromJSON(e))
        : [],
    };
  },

  toJSON(message: RigActivityConfig): unknown {
    const obj: any = {};
    if (message.activity !== 0) {
      obj.activity = autonomousActivityTypeToJSON(message.activity);
    }
    if (message.constraints !== undefined) {
      obj.constraints = RigActivityConstraints.toJSON(message.constraints);
    }
    if (message.quickActionStages?.length) {
      obj.quickActionStages = message.quickActionStages.map((e) => QuickActionStage.toJSON(e));
    }
    if (message.quickActionTime !== undefined) {
      obj.quickActionTime = Duration.toJSON(message.quickActionTime);
    }
    if (message.ongoingActions?.length) {
      obj.ongoingActions = message.ongoingActions.map((e) => OngoingAction.toJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<RigActivityConfig>, I>>(base?: I): RigActivityConfig {
    return RigActivityConfig.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<RigActivityConfig>, I>>(object: I): RigActivityConfig {
    const message = createBaseRigActivityConfig();
    message.activity = object.activity ?? 0;
    message.constraints = (object.constraints !== undefined && object.constraints !== null)
      ? RigActivityConstraints.fromPartial(object.constraints)
      : undefined;
    message.quickActionStages = object.quickActionStages?.map((e) => QuickActionStage.fromPartial(e)) || [];
    message.quickActionTime = (object.quickActionTime !== undefined && object.quickActionTime !== null)
      ? Duration.fromPartial(object.quickActionTime)
      : undefined;
    message.ongoingActions = object.ongoingActions?.map((e) => OngoingAction.fromPartial(e)) || [];
    return message;
  },
};

function createBaseRigActivityConstraints(): RigActivityConstraints {
  return {
    maxHitchAngleRad: 0,
    minTurnRadiusM: undefined,
    workingSpeedMps: 0,
    turningSpeedMps: 0,
    transitionSpeedMps: 0,
  };
}

export const RigActivityConstraints: MessageFns<RigActivityConstraints> = {
  encode(message: RigActivityConstraints, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.maxHitchAngleRad !== 0) {
      writer.uint32(13).float(message.maxHitchAngleRad);
    }
    if (message.minTurnRadiusM !== undefined) {
      writer.uint32(21).float(message.minTurnRadiusM);
    }
    if (message.workingSpeedMps !== 0) {
      writer.uint32(29).float(message.workingSpeedMps);
    }
    if (message.turningSpeedMps !== 0) {
      writer.uint32(45).float(message.turningSpeedMps);
    }
    if (message.transitionSpeedMps !== 0) {
      writer.uint32(37).float(message.transitionSpeedMps);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): RigActivityConstraints {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseRigActivityConstraints();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 13) {
            break;
          }

          message.maxHitchAngleRad = reader.float();
          continue;
        }
        case 2: {
          if (tag !== 21) {
            break;
          }

          message.minTurnRadiusM = reader.float();
          continue;
        }
        case 3: {
          if (tag !== 29) {
            break;
          }

          message.workingSpeedMps = reader.float();
          continue;
        }
        case 5: {
          if (tag !== 45) {
            break;
          }

          message.turningSpeedMps = reader.float();
          continue;
        }
        case 4: {
          if (tag !== 37) {
            break;
          }

          message.transitionSpeedMps = reader.float();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): RigActivityConstraints {
    return {
      maxHitchAngleRad: isSet(object.maxHitchAngleRad)
        ? globalThis.Number(object.maxHitchAngleRad)
        : isSet(object.max_hitch_angle_rad)
        ? globalThis.Number(object.max_hitch_angle_rad)
        : 0,
      minTurnRadiusM: isSet(object.minTurnRadiusM)
        ? globalThis.Number(object.minTurnRadiusM)
        : isSet(object.min_turn_radius_m)
        ? globalThis.Number(object.min_turn_radius_m)
        : undefined,
      workingSpeedMps: isSet(object.workingSpeedMps)
        ? globalThis.Number(object.workingSpeedMps)
        : isSet(object.working_speed_mps)
        ? globalThis.Number(object.working_speed_mps)
        : 0,
      turningSpeedMps: isSet(object.turningSpeedMps)
        ? globalThis.Number(object.turningSpeedMps)
        : isSet(object.turning_speed_mps)
        ? globalThis.Number(object.turning_speed_mps)
        : 0,
      transitionSpeedMps: isSet(object.transitionSpeedMps)
        ? globalThis.Number(object.transitionSpeedMps)
        : isSet(object.transition_speed_mps)
        ? globalThis.Number(object.transition_speed_mps)
        : 0,
    };
  },

  toJSON(message: RigActivityConstraints): unknown {
    const obj: any = {};
    if (message.maxHitchAngleRad !== 0) {
      obj.maxHitchAngleRad = message.maxHitchAngleRad;
    }
    if (message.minTurnRadiusM !== undefined) {
      obj.minTurnRadiusM = message.minTurnRadiusM;
    }
    if (message.workingSpeedMps !== 0) {
      obj.workingSpeedMps = message.workingSpeedMps;
    }
    if (message.turningSpeedMps !== 0) {
      obj.turningSpeedMps = message.turningSpeedMps;
    }
    if (message.transitionSpeedMps !== 0) {
      obj.transitionSpeedMps = message.transitionSpeedMps;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<RigActivityConstraints>, I>>(base?: I): RigActivityConstraints {
    return RigActivityConstraints.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<RigActivityConstraints>, I>>(object: I): RigActivityConstraints {
    const message = createBaseRigActivityConstraints();
    message.maxHitchAngleRad = object.maxHitchAngleRad ?? 0;
    message.minTurnRadiusM = object.minTurnRadiusM ?? undefined;
    message.workingSpeedMps = object.workingSpeedMps ?? 0;
    message.turningSpeedMps = object.turningSpeedMps ?? 0;
    message.transitionSpeedMps = object.transitionSpeedMps ?? 0;
    return message;
  },
};

function createBaseRigActivitiesConfiguration(): RigActivitiesConfiguration {
  return { activities: [] };
}

export const RigActivitiesConfiguration: MessageFns<RigActivitiesConfiguration> = {
  encode(message: RigActivitiesConfiguration, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    for (const v of message.activities) {
      RigActivityConfig.encode(v!, writer.uint32(10).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): RigActivitiesConfiguration {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseRigActivitiesConfiguration();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.activities.push(RigActivityConfig.decode(reader, reader.uint32()));
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): RigActivitiesConfiguration {
    return {
      activities: globalThis.Array.isArray(object?.activities)
        ? object.activities.map((e: any) => RigActivityConfig.fromJSON(e))
        : [],
    };
  },

  toJSON(message: RigActivitiesConfiguration): unknown {
    const obj: any = {};
    if (message.activities?.length) {
      obj.activities = message.activities.map((e) => RigActivityConfig.toJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<RigActivitiesConfiguration>, I>>(base?: I): RigActivitiesConfiguration {
    return RigActivitiesConfiguration.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<RigActivitiesConfiguration>, I>>(object: I): RigActivitiesConfiguration {
    const message = createBaseRigActivitiesConfiguration();
    message.activities = object.activities?.map((e) => RigActivityConfig.fromPartial(e)) || [];
    return message;
  },
};

function createBaseQuickAction(): QuickAction {
  return {
    actionId: "",
    scvDetentUp: undefined,
    scvDetentDown: undefined,
    scvTimedForce: undefined,
    scvConstantForce: undefined,
    scvMoveToPosition: undefined,
    scvFloat: undefined,
    ptoOn: undefined,
    ptoOff: undefined,
    hitchDetentUp: undefined,
    hitchDetentDown: undefined,
    hitchTimedForce: undefined,
    hitchSetPosition: undefined,
    hitchFloat: undefined,
    laserweederOn: undefined,
    laserweederEstopOn: undefined,
    laserweederEstopOff: undefined,
    wait: undefined,
    setEngineRpmToMax: undefined,
    setEngineRpmToMin: undefined,
    advanced: undefined,
    startDelay: undefined,
    carryOverIntoWorkTask: false,
  };
}

export const QuickAction: MessageFns<QuickAction> = {
  encode(message: QuickAction, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.actionId !== "") {
      writer.uint32(10).string(message.actionId);
    }
    if (message.scvDetentUp !== undefined) {
      ScvDetentUp.encode(message.scvDetentUp, writer.uint32(18).fork()).join();
    }
    if (message.scvDetentDown !== undefined) {
      ScvDetentDown.encode(message.scvDetentDown, writer.uint32(26).fork()).join();
    }
    if (message.scvTimedForce !== undefined) {
      ScvTimedForce.encode(message.scvTimedForce, writer.uint32(34).fork()).join();
    }
    if (message.scvConstantForce !== undefined) {
      ScvConstantForce.encode(message.scvConstantForce, writer.uint32(234).fork()).join();
    }
    if (message.scvMoveToPosition !== undefined) {
      ScvMoveToPosition.encode(message.scvMoveToPosition, writer.uint32(42).fork()).join();
    }
    if (message.scvFloat !== undefined) {
      ScvFloat.encode(message.scvFloat, writer.uint32(194).fork()).join();
    }
    if (message.ptoOn !== undefined) {
      PtoOn.encode(message.ptoOn, writer.uint32(58).fork()).join();
    }
    if (message.ptoOff !== undefined) {
      PtoOff.encode(message.ptoOff, writer.uint32(66).fork()).join();
    }
    if (message.hitchDetentUp !== undefined) {
      HitchDetentUp.encode(message.hitchDetentUp, writer.uint32(74).fork()).join();
    }
    if (message.hitchDetentDown !== undefined) {
      HitchDetentDown.encode(message.hitchDetentDown, writer.uint32(82).fork()).join();
    }
    if (message.hitchTimedForce !== undefined) {
      HitchTimedForce.encode(message.hitchTimedForce, writer.uint32(90).fork()).join();
    }
    if (message.hitchSetPosition !== undefined) {
      HitchHoldPosition.encode(message.hitchSetPosition, writer.uint32(218).fork()).join();
    }
    if (message.hitchFloat !== undefined) {
      HitchFloat.encode(message.hitchFloat, writer.uint32(202).fork()).join();
    }
    if (message.laserweederOn !== undefined) {
      LaserweederOn.encode(message.laserweederOn, writer.uint32(210).fork()).join();
    }
    if (message.laserweederEstopOn !== undefined) {
      LaserWeederEstopOn.encode(message.laserweederEstopOn, writer.uint32(98).fork()).join();
    }
    if (message.laserweederEstopOff !== undefined) {
      LaserWeederEstopOff.encode(message.laserweederEstopOff, writer.uint32(106).fork()).join();
    }
    if (message.wait !== undefined) {
      Wait.encode(message.wait, writer.uint32(138).fork()).join();
    }
    if (message.setEngineRpmToMax !== undefined) {
      SetEngineRpmToMax.encode(message.setEngineRpmToMax, writer.uint32(146).fork()).join();
    }
    if (message.setEngineRpmToMin !== undefined) {
      SetEngineRpmToMin.encode(message.setEngineRpmToMin, writer.uint32(154).fork()).join();
    }
    if (message.advanced !== undefined) {
      AdvancedAction.encode(message.advanced, writer.uint32(170).fork()).join();
    }
    if (message.startDelay !== undefined) {
      Duration.encode(message.startDelay, writer.uint32(178).fork()).join();
    }
    if (message.carryOverIntoWorkTask !== false) {
      writer.uint32(224).bool(message.carryOverIntoWorkTask);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): QuickAction {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseQuickAction();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.actionId = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.scvDetentUp = ScvDetentUp.decode(reader, reader.uint32());
          continue;
        }
        case 3: {
          if (tag !== 26) {
            break;
          }

          message.scvDetentDown = ScvDetentDown.decode(reader, reader.uint32());
          continue;
        }
        case 4: {
          if (tag !== 34) {
            break;
          }

          message.scvTimedForce = ScvTimedForce.decode(reader, reader.uint32());
          continue;
        }
        case 29: {
          if (tag !== 234) {
            break;
          }

          message.scvConstantForce = ScvConstantForce.decode(reader, reader.uint32());
          continue;
        }
        case 5: {
          if (tag !== 42) {
            break;
          }

          message.scvMoveToPosition = ScvMoveToPosition.decode(reader, reader.uint32());
          continue;
        }
        case 24: {
          if (tag !== 194) {
            break;
          }

          message.scvFloat = ScvFloat.decode(reader, reader.uint32());
          continue;
        }
        case 7: {
          if (tag !== 58) {
            break;
          }

          message.ptoOn = PtoOn.decode(reader, reader.uint32());
          continue;
        }
        case 8: {
          if (tag !== 66) {
            break;
          }

          message.ptoOff = PtoOff.decode(reader, reader.uint32());
          continue;
        }
        case 9: {
          if (tag !== 74) {
            break;
          }

          message.hitchDetentUp = HitchDetentUp.decode(reader, reader.uint32());
          continue;
        }
        case 10: {
          if (tag !== 82) {
            break;
          }

          message.hitchDetentDown = HitchDetentDown.decode(reader, reader.uint32());
          continue;
        }
        case 11: {
          if (tag !== 90) {
            break;
          }

          message.hitchTimedForce = HitchTimedForce.decode(reader, reader.uint32());
          continue;
        }
        case 27: {
          if (tag !== 218) {
            break;
          }

          message.hitchSetPosition = HitchHoldPosition.decode(reader, reader.uint32());
          continue;
        }
        case 25: {
          if (tag !== 202) {
            break;
          }

          message.hitchFloat = HitchFloat.decode(reader, reader.uint32());
          continue;
        }
        case 26: {
          if (tag !== 210) {
            break;
          }

          message.laserweederOn = LaserweederOn.decode(reader, reader.uint32());
          continue;
        }
        case 12: {
          if (tag !== 98) {
            break;
          }

          message.laserweederEstopOn = LaserWeederEstopOn.decode(reader, reader.uint32());
          continue;
        }
        case 13: {
          if (tag !== 106) {
            break;
          }

          message.laserweederEstopOff = LaserWeederEstopOff.decode(reader, reader.uint32());
          continue;
        }
        case 17: {
          if (tag !== 138) {
            break;
          }

          message.wait = Wait.decode(reader, reader.uint32());
          continue;
        }
        case 18: {
          if (tag !== 146) {
            break;
          }

          message.setEngineRpmToMax = SetEngineRpmToMax.decode(reader, reader.uint32());
          continue;
        }
        case 19: {
          if (tag !== 154) {
            break;
          }

          message.setEngineRpmToMin = SetEngineRpmToMin.decode(reader, reader.uint32());
          continue;
        }
        case 21: {
          if (tag !== 170) {
            break;
          }

          message.advanced = AdvancedAction.decode(reader, reader.uint32());
          continue;
        }
        case 22: {
          if (tag !== 178) {
            break;
          }

          message.startDelay = Duration.decode(reader, reader.uint32());
          continue;
        }
        case 28: {
          if (tag !== 224) {
            break;
          }

          message.carryOverIntoWorkTask = reader.bool();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): QuickAction {
    return {
      actionId: isSet(object.actionId)
        ? globalThis.String(object.actionId)
        : isSet(object.action_id)
        ? globalThis.String(object.action_id)
        : "",
      scvDetentUp: isSet(object.scvDetentUp)
        ? ScvDetentUp.fromJSON(object.scvDetentUp)
        : isSet(object.scv_detent_up)
        ? ScvDetentUp.fromJSON(object.scv_detent_up)
        : undefined,
      scvDetentDown: isSet(object.scvDetentDown)
        ? ScvDetentDown.fromJSON(object.scvDetentDown)
        : isSet(object.scv_detent_down)
        ? ScvDetentDown.fromJSON(object.scv_detent_down)
        : undefined,
      scvTimedForce: isSet(object.scvTimedForce)
        ? ScvTimedForce.fromJSON(object.scvTimedForce)
        : isSet(object.scv_timed_force)
        ? ScvTimedForce.fromJSON(object.scv_timed_force)
        : undefined,
      scvConstantForce: isSet(object.scvConstantForce)
        ? ScvConstantForce.fromJSON(object.scvConstantForce)
        : isSet(object.scv_constant_force)
        ? ScvConstantForce.fromJSON(object.scv_constant_force)
        : undefined,
      scvMoveToPosition: isSet(object.scvMoveToPosition)
        ? ScvMoveToPosition.fromJSON(object.scvMoveToPosition)
        : isSet(object.scv_move_to_position)
        ? ScvMoveToPosition.fromJSON(object.scv_move_to_position)
        : undefined,
      scvFloat: isSet(object.scvFloat)
        ? ScvFloat.fromJSON(object.scvFloat)
        : isSet(object.scv_float)
        ? ScvFloat.fromJSON(object.scv_float)
        : undefined,
      ptoOn: isSet(object.ptoOn)
        ? PtoOn.fromJSON(object.ptoOn)
        : isSet(object.pto_on)
        ? PtoOn.fromJSON(object.pto_on)
        : undefined,
      ptoOff: isSet(object.ptoOff)
        ? PtoOff.fromJSON(object.ptoOff)
        : isSet(object.pto_off)
        ? PtoOff.fromJSON(object.pto_off)
        : undefined,
      hitchDetentUp: isSet(object.hitchDetentUp)
        ? HitchDetentUp.fromJSON(object.hitchDetentUp)
        : isSet(object.hitch_detent_up)
        ? HitchDetentUp.fromJSON(object.hitch_detent_up)
        : undefined,
      hitchDetentDown: isSet(object.hitchDetentDown)
        ? HitchDetentDown.fromJSON(object.hitchDetentDown)
        : isSet(object.hitch_detent_down)
        ? HitchDetentDown.fromJSON(object.hitch_detent_down)
        : undefined,
      hitchTimedForce: isSet(object.hitchTimedForce)
        ? HitchTimedForce.fromJSON(object.hitchTimedForce)
        : isSet(object.hitch_timed_force)
        ? HitchTimedForce.fromJSON(object.hitch_timed_force)
        : undefined,
      hitchSetPosition: isSet(object.hitchSetPosition)
        ? HitchHoldPosition.fromJSON(object.hitchSetPosition)
        : isSet(object.hitch_set_position)
        ? HitchHoldPosition.fromJSON(object.hitch_set_position)
        : undefined,
      hitchFloat: isSet(object.hitchFloat)
        ? HitchFloat.fromJSON(object.hitchFloat)
        : isSet(object.hitch_float)
        ? HitchFloat.fromJSON(object.hitch_float)
        : undefined,
      laserweederOn: isSet(object.laserweederOn)
        ? LaserweederOn.fromJSON(object.laserweederOn)
        : isSet(object.laserweeder_on)
        ? LaserweederOn.fromJSON(object.laserweeder_on)
        : undefined,
      laserweederEstopOn: isSet(object.laserweederEstopOn)
        ? LaserWeederEstopOn.fromJSON(object.laserweederEstopOn)
        : isSet(object.laserweeder_estop_on)
        ? LaserWeederEstopOn.fromJSON(object.laserweeder_estop_on)
        : undefined,
      laserweederEstopOff: isSet(object.laserweederEstopOff)
        ? LaserWeederEstopOff.fromJSON(object.laserweederEstopOff)
        : isSet(object.laserweeder_estop_off)
        ? LaserWeederEstopOff.fromJSON(object.laserweeder_estop_off)
        : undefined,
      wait: isSet(object.wait) ? Wait.fromJSON(object.wait) : undefined,
      setEngineRpmToMax: isSet(object.setEngineRpmToMax)
        ? SetEngineRpmToMax.fromJSON(object.setEngineRpmToMax)
        : isSet(object.set_engine_rpm_to_max)
        ? SetEngineRpmToMax.fromJSON(object.set_engine_rpm_to_max)
        : undefined,
      setEngineRpmToMin: isSet(object.setEngineRpmToMin)
        ? SetEngineRpmToMin.fromJSON(object.setEngineRpmToMin)
        : isSet(object.set_engine_rpm_to_min)
        ? SetEngineRpmToMin.fromJSON(object.set_engine_rpm_to_min)
        : undefined,
      advanced: isSet(object.advanced) ? AdvancedAction.fromJSON(object.advanced) : undefined,
      startDelay: isSet(object.startDelay)
        ? Duration.fromJSON(object.startDelay)
        : isSet(object.start_delay)
        ? Duration.fromJSON(object.start_delay)
        : undefined,
      carryOverIntoWorkTask: isSet(object.carryOverIntoWorkTask)
        ? globalThis.Boolean(object.carryOverIntoWorkTask)
        : isSet(object.carry_over_into_work_task)
        ? globalThis.Boolean(object.carry_over_into_work_task)
        : false,
    };
  },

  toJSON(message: QuickAction): unknown {
    const obj: any = {};
    if (message.actionId !== "") {
      obj.actionId = message.actionId;
    }
    if (message.scvDetentUp !== undefined) {
      obj.scvDetentUp = ScvDetentUp.toJSON(message.scvDetentUp);
    }
    if (message.scvDetentDown !== undefined) {
      obj.scvDetentDown = ScvDetentDown.toJSON(message.scvDetentDown);
    }
    if (message.scvTimedForce !== undefined) {
      obj.scvTimedForce = ScvTimedForce.toJSON(message.scvTimedForce);
    }
    if (message.scvConstantForce !== undefined) {
      obj.scvConstantForce = ScvConstantForce.toJSON(message.scvConstantForce);
    }
    if (message.scvMoveToPosition !== undefined) {
      obj.scvMoveToPosition = ScvMoveToPosition.toJSON(message.scvMoveToPosition);
    }
    if (message.scvFloat !== undefined) {
      obj.scvFloat = ScvFloat.toJSON(message.scvFloat);
    }
    if (message.ptoOn !== undefined) {
      obj.ptoOn = PtoOn.toJSON(message.ptoOn);
    }
    if (message.ptoOff !== undefined) {
      obj.ptoOff = PtoOff.toJSON(message.ptoOff);
    }
    if (message.hitchDetentUp !== undefined) {
      obj.hitchDetentUp = HitchDetentUp.toJSON(message.hitchDetentUp);
    }
    if (message.hitchDetentDown !== undefined) {
      obj.hitchDetentDown = HitchDetentDown.toJSON(message.hitchDetentDown);
    }
    if (message.hitchTimedForce !== undefined) {
      obj.hitchTimedForce = HitchTimedForce.toJSON(message.hitchTimedForce);
    }
    if (message.hitchSetPosition !== undefined) {
      obj.hitchSetPosition = HitchHoldPosition.toJSON(message.hitchSetPosition);
    }
    if (message.hitchFloat !== undefined) {
      obj.hitchFloat = HitchFloat.toJSON(message.hitchFloat);
    }
    if (message.laserweederOn !== undefined) {
      obj.laserweederOn = LaserweederOn.toJSON(message.laserweederOn);
    }
    if (message.laserweederEstopOn !== undefined) {
      obj.laserweederEstopOn = LaserWeederEstopOn.toJSON(message.laserweederEstopOn);
    }
    if (message.laserweederEstopOff !== undefined) {
      obj.laserweederEstopOff = LaserWeederEstopOff.toJSON(message.laserweederEstopOff);
    }
    if (message.wait !== undefined) {
      obj.wait = Wait.toJSON(message.wait);
    }
    if (message.setEngineRpmToMax !== undefined) {
      obj.setEngineRpmToMax = SetEngineRpmToMax.toJSON(message.setEngineRpmToMax);
    }
    if (message.setEngineRpmToMin !== undefined) {
      obj.setEngineRpmToMin = SetEngineRpmToMin.toJSON(message.setEngineRpmToMin);
    }
    if (message.advanced !== undefined) {
      obj.advanced = AdvancedAction.toJSON(message.advanced);
    }
    if (message.startDelay !== undefined) {
      obj.startDelay = Duration.toJSON(message.startDelay);
    }
    if (message.carryOverIntoWorkTask !== false) {
      obj.carryOverIntoWorkTask = message.carryOverIntoWorkTask;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<QuickAction>, I>>(base?: I): QuickAction {
    return QuickAction.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<QuickAction>, I>>(object: I): QuickAction {
    const message = createBaseQuickAction();
    message.actionId = object.actionId ?? "";
    message.scvDetentUp = (object.scvDetentUp !== undefined && object.scvDetentUp !== null)
      ? ScvDetentUp.fromPartial(object.scvDetentUp)
      : undefined;
    message.scvDetentDown = (object.scvDetentDown !== undefined && object.scvDetentDown !== null)
      ? ScvDetentDown.fromPartial(object.scvDetentDown)
      : undefined;
    message.scvTimedForce = (object.scvTimedForce !== undefined && object.scvTimedForce !== null)
      ? ScvTimedForce.fromPartial(object.scvTimedForce)
      : undefined;
    message.scvConstantForce = (object.scvConstantForce !== undefined && object.scvConstantForce !== null)
      ? ScvConstantForce.fromPartial(object.scvConstantForce)
      : undefined;
    message.scvMoveToPosition = (object.scvMoveToPosition !== undefined && object.scvMoveToPosition !== null)
      ? ScvMoveToPosition.fromPartial(object.scvMoveToPosition)
      : undefined;
    message.scvFloat = (object.scvFloat !== undefined && object.scvFloat !== null)
      ? ScvFloat.fromPartial(object.scvFloat)
      : undefined;
    message.ptoOn = (object.ptoOn !== undefined && object.ptoOn !== null) ? PtoOn.fromPartial(object.ptoOn) : undefined;
    message.ptoOff = (object.ptoOff !== undefined && object.ptoOff !== null)
      ? PtoOff.fromPartial(object.ptoOff)
      : undefined;
    message.hitchDetentUp = (object.hitchDetentUp !== undefined && object.hitchDetentUp !== null)
      ? HitchDetentUp.fromPartial(object.hitchDetentUp)
      : undefined;
    message.hitchDetentDown = (object.hitchDetentDown !== undefined && object.hitchDetentDown !== null)
      ? HitchDetentDown.fromPartial(object.hitchDetentDown)
      : undefined;
    message.hitchTimedForce = (object.hitchTimedForce !== undefined && object.hitchTimedForce !== null)
      ? HitchTimedForce.fromPartial(object.hitchTimedForce)
      : undefined;
    message.hitchSetPosition = (object.hitchSetPosition !== undefined && object.hitchSetPosition !== null)
      ? HitchHoldPosition.fromPartial(object.hitchSetPosition)
      : undefined;
    message.hitchFloat = (object.hitchFloat !== undefined && object.hitchFloat !== null)
      ? HitchFloat.fromPartial(object.hitchFloat)
      : undefined;
    message.laserweederOn = (object.laserweederOn !== undefined && object.laserweederOn !== null)
      ? LaserweederOn.fromPartial(object.laserweederOn)
      : undefined;
    message.laserweederEstopOn = (object.laserweederEstopOn !== undefined && object.laserweederEstopOn !== null)
      ? LaserWeederEstopOn.fromPartial(object.laserweederEstopOn)
      : undefined;
    message.laserweederEstopOff = (object.laserweederEstopOff !== undefined && object.laserweederEstopOff !== null)
      ? LaserWeederEstopOff.fromPartial(object.laserweederEstopOff)
      : undefined;
    message.wait = (object.wait !== undefined && object.wait !== null) ? Wait.fromPartial(object.wait) : undefined;
    message.setEngineRpmToMax = (object.setEngineRpmToMax !== undefined && object.setEngineRpmToMax !== null)
      ? SetEngineRpmToMax.fromPartial(object.setEngineRpmToMax)
      : undefined;
    message.setEngineRpmToMin = (object.setEngineRpmToMin !== undefined && object.setEngineRpmToMin !== null)
      ? SetEngineRpmToMin.fromPartial(object.setEngineRpmToMin)
      : undefined;
    message.advanced = (object.advanced !== undefined && object.advanced !== null)
      ? AdvancedAction.fromPartial(object.advanced)
      : undefined;
    message.startDelay = (object.startDelay !== undefined && object.startDelay !== null)
      ? Duration.fromPartial(object.startDelay)
      : undefined;
    message.carryOverIntoWorkTask = object.carryOverIntoWorkTask ?? false;
    return message;
  },
};

function createBaseQuickActionStage(): QuickActionStage {
  return { actions: [] };
}

export const QuickActionStage: MessageFns<QuickActionStage> = {
  encode(message: QuickActionStage, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    for (const v of message.actions) {
      QuickAction.encode(v!, writer.uint32(10).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): QuickActionStage {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseQuickActionStage();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.actions.push(QuickAction.decode(reader, reader.uint32()));
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): QuickActionStage {
    return {
      actions: globalThis.Array.isArray(object?.actions) ? object.actions.map((e: any) => QuickAction.fromJSON(e)) : [],
    };
  },

  toJSON(message: QuickActionStage): unknown {
    const obj: any = {};
    if (message.actions?.length) {
      obj.actions = message.actions.map((e) => QuickAction.toJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<QuickActionStage>, I>>(base?: I): QuickActionStage {
    return QuickActionStage.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<QuickActionStage>, I>>(object: I): QuickActionStage {
    const message = createBaseQuickActionStage();
    message.actions = object.actions?.map((e) => QuickAction.fromPartial(e)) || [];
    return message;
  },
};

function createBaseOngoingAction(): OngoingAction {
  return {
    actionId: "",
    scvConstantForce: undefined,
    scvHoldPosition: undefined,
    scvFloat: undefined,
    ptoOn: undefined,
    hitchHoldPosition: undefined,
    hitchFloat: undefined,
    laserweederEstopOn: undefined,
    laserweederEstopOff: undefined,
    laserweederOn: undefined,
    advanced: undefined,
  };
}

export const OngoingAction: MessageFns<OngoingAction> = {
  encode(message: OngoingAction, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.actionId !== "") {
      writer.uint32(10).string(message.actionId);
    }
    if (message.scvConstantForce !== undefined) {
      ScvConstantForce.encode(message.scvConstantForce, writer.uint32(18).fork()).join();
    }
    if (message.scvHoldPosition !== undefined) {
      ScvHoldPosition.encode(message.scvHoldPosition, writer.uint32(26).fork()).join();
    }
    if (message.scvFloat !== undefined) {
      ScvFloat.encode(message.scvFloat, writer.uint32(34).fork()).join();
    }
    if (message.ptoOn !== undefined) {
      PtoOn.encode(message.ptoOn, writer.uint32(98).fork()).join();
    }
    if (message.hitchHoldPosition !== undefined) {
      HitchHoldPosition.encode(message.hitchHoldPosition, writer.uint32(50).fork()).join();
    }
    if (message.hitchFloat !== undefined) {
      HitchFloat.encode(message.hitchFloat, writer.uint32(58).fork()).join();
    }
    if (message.laserweederEstopOn !== undefined) {
      LaserWeederEstopOn.encode(message.laserweederEstopOn, writer.uint32(106).fork()).join();
    }
    if (message.laserweederEstopOff !== undefined) {
      LaserWeederEstopOff.encode(message.laserweederEstopOff, writer.uint32(114).fork()).join();
    }
    if (message.laserweederOn !== undefined) {
      LaserweederOn.encode(message.laserweederOn, writer.uint32(74).fork()).join();
    }
    if (message.advanced !== undefined) {
      AdvancedAction.encode(message.advanced, writer.uint32(90).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): OngoingAction {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseOngoingAction();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.actionId = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.scvConstantForce = ScvConstantForce.decode(reader, reader.uint32());
          continue;
        }
        case 3: {
          if (tag !== 26) {
            break;
          }

          message.scvHoldPosition = ScvHoldPosition.decode(reader, reader.uint32());
          continue;
        }
        case 4: {
          if (tag !== 34) {
            break;
          }

          message.scvFloat = ScvFloat.decode(reader, reader.uint32());
          continue;
        }
        case 12: {
          if (tag !== 98) {
            break;
          }

          message.ptoOn = PtoOn.decode(reader, reader.uint32());
          continue;
        }
        case 6: {
          if (tag !== 50) {
            break;
          }

          message.hitchHoldPosition = HitchHoldPosition.decode(reader, reader.uint32());
          continue;
        }
        case 7: {
          if (tag !== 58) {
            break;
          }

          message.hitchFloat = HitchFloat.decode(reader, reader.uint32());
          continue;
        }
        case 13: {
          if (tag !== 106) {
            break;
          }

          message.laserweederEstopOn = LaserWeederEstopOn.decode(reader, reader.uint32());
          continue;
        }
        case 14: {
          if (tag !== 114) {
            break;
          }

          message.laserweederEstopOff = LaserWeederEstopOff.decode(reader, reader.uint32());
          continue;
        }
        case 9: {
          if (tag !== 74) {
            break;
          }

          message.laserweederOn = LaserweederOn.decode(reader, reader.uint32());
          continue;
        }
        case 11: {
          if (tag !== 90) {
            break;
          }

          message.advanced = AdvancedAction.decode(reader, reader.uint32());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): OngoingAction {
    return {
      actionId: isSet(object.actionId)
        ? globalThis.String(object.actionId)
        : isSet(object.action_id)
        ? globalThis.String(object.action_id)
        : "",
      scvConstantForce: isSet(object.scvConstantForce)
        ? ScvConstantForce.fromJSON(object.scvConstantForce)
        : isSet(object.scv_constant_force)
        ? ScvConstantForce.fromJSON(object.scv_constant_force)
        : undefined,
      scvHoldPosition: isSet(object.scvHoldPosition)
        ? ScvHoldPosition.fromJSON(object.scvHoldPosition)
        : isSet(object.scv_hold_position)
        ? ScvHoldPosition.fromJSON(object.scv_hold_position)
        : undefined,
      scvFloat: isSet(object.scvFloat)
        ? ScvFloat.fromJSON(object.scvFloat)
        : isSet(object.scv_float)
        ? ScvFloat.fromJSON(object.scv_float)
        : undefined,
      ptoOn: isSet(object.ptoOn)
        ? PtoOn.fromJSON(object.ptoOn)
        : isSet(object.pto_on)
        ? PtoOn.fromJSON(object.pto_on)
        : undefined,
      hitchHoldPosition: isSet(object.hitchHoldPosition)
        ? HitchHoldPosition.fromJSON(object.hitchHoldPosition)
        : isSet(object.hitch_hold_position)
        ? HitchHoldPosition.fromJSON(object.hitch_hold_position)
        : undefined,
      hitchFloat: isSet(object.hitchFloat)
        ? HitchFloat.fromJSON(object.hitchFloat)
        : isSet(object.hitch_float)
        ? HitchFloat.fromJSON(object.hitch_float)
        : undefined,
      laserweederEstopOn: isSet(object.laserweederEstopOn)
        ? LaserWeederEstopOn.fromJSON(object.laserweederEstopOn)
        : isSet(object.laserweeder_estop_on)
        ? LaserWeederEstopOn.fromJSON(object.laserweeder_estop_on)
        : undefined,
      laserweederEstopOff: isSet(object.laserweederEstopOff)
        ? LaserWeederEstopOff.fromJSON(object.laserweederEstopOff)
        : isSet(object.laserweeder_estop_off)
        ? LaserWeederEstopOff.fromJSON(object.laserweeder_estop_off)
        : undefined,
      laserweederOn: isSet(object.laserweederOn)
        ? LaserweederOn.fromJSON(object.laserweederOn)
        : isSet(object.laserweeder_on)
        ? LaserweederOn.fromJSON(object.laserweeder_on)
        : undefined,
      advanced: isSet(object.advanced) ? AdvancedAction.fromJSON(object.advanced) : undefined,
    };
  },

  toJSON(message: OngoingAction): unknown {
    const obj: any = {};
    if (message.actionId !== "") {
      obj.actionId = message.actionId;
    }
    if (message.scvConstantForce !== undefined) {
      obj.scvConstantForce = ScvConstantForce.toJSON(message.scvConstantForce);
    }
    if (message.scvHoldPosition !== undefined) {
      obj.scvHoldPosition = ScvHoldPosition.toJSON(message.scvHoldPosition);
    }
    if (message.scvFloat !== undefined) {
      obj.scvFloat = ScvFloat.toJSON(message.scvFloat);
    }
    if (message.ptoOn !== undefined) {
      obj.ptoOn = PtoOn.toJSON(message.ptoOn);
    }
    if (message.hitchHoldPosition !== undefined) {
      obj.hitchHoldPosition = HitchHoldPosition.toJSON(message.hitchHoldPosition);
    }
    if (message.hitchFloat !== undefined) {
      obj.hitchFloat = HitchFloat.toJSON(message.hitchFloat);
    }
    if (message.laserweederEstopOn !== undefined) {
      obj.laserweederEstopOn = LaserWeederEstopOn.toJSON(message.laserweederEstopOn);
    }
    if (message.laserweederEstopOff !== undefined) {
      obj.laserweederEstopOff = LaserWeederEstopOff.toJSON(message.laserweederEstopOff);
    }
    if (message.laserweederOn !== undefined) {
      obj.laserweederOn = LaserweederOn.toJSON(message.laserweederOn);
    }
    if (message.advanced !== undefined) {
      obj.advanced = AdvancedAction.toJSON(message.advanced);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<OngoingAction>, I>>(base?: I): OngoingAction {
    return OngoingAction.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<OngoingAction>, I>>(object: I): OngoingAction {
    const message = createBaseOngoingAction();
    message.actionId = object.actionId ?? "";
    message.scvConstantForce = (object.scvConstantForce !== undefined && object.scvConstantForce !== null)
      ? ScvConstantForce.fromPartial(object.scvConstantForce)
      : undefined;
    message.scvHoldPosition = (object.scvHoldPosition !== undefined && object.scvHoldPosition !== null)
      ? ScvHoldPosition.fromPartial(object.scvHoldPosition)
      : undefined;
    message.scvFloat = (object.scvFloat !== undefined && object.scvFloat !== null)
      ? ScvFloat.fromPartial(object.scvFloat)
      : undefined;
    message.ptoOn = (object.ptoOn !== undefined && object.ptoOn !== null) ? PtoOn.fromPartial(object.ptoOn) : undefined;
    message.hitchHoldPosition = (object.hitchHoldPosition !== undefined && object.hitchHoldPosition !== null)
      ? HitchHoldPosition.fromPartial(object.hitchHoldPosition)
      : undefined;
    message.hitchFloat = (object.hitchFloat !== undefined && object.hitchFloat !== null)
      ? HitchFloat.fromPartial(object.hitchFloat)
      : undefined;
    message.laserweederEstopOn = (object.laserweederEstopOn !== undefined && object.laserweederEstopOn !== null)
      ? LaserWeederEstopOn.fromPartial(object.laserweederEstopOn)
      : undefined;
    message.laserweederEstopOff = (object.laserweederEstopOff !== undefined && object.laserweederEstopOff !== null)
      ? LaserWeederEstopOff.fromPartial(object.laserweederEstopOff)
      : undefined;
    message.laserweederOn = (object.laserweederOn !== undefined && object.laserweederOn !== null)
      ? LaserweederOn.fromPartial(object.laserweederOn)
      : undefined;
    message.advanced = (object.advanced !== undefined && object.advanced !== null)
      ? AdvancedAction.fromPartial(object.advanced)
      : undefined;
    return message;
  },
};

function createBaseScvDetentUp(): ScvDetentUp {
  return { scvInputId: "", vtDetentTime: undefined };
}

export const ScvDetentUp: MessageFns<ScvDetentUp> = {
  encode(message: ScvDetentUp, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.scvInputId !== "") {
      writer.uint32(10).string(message.scvInputId);
    }
    if (message.vtDetentTime !== undefined) {
      Duration.encode(message.vtDetentTime, writer.uint32(18).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): ScvDetentUp {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseScvDetentUp();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.scvInputId = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.vtDetentTime = Duration.decode(reader, reader.uint32());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ScvDetentUp {
    return {
      scvInputId: isSet(object.scvInputId)
        ? globalThis.String(object.scvInputId)
        : isSet(object.scv_input_id)
        ? globalThis.String(object.scv_input_id)
        : "",
      vtDetentTime: isSet(object.vtDetentTime)
        ? Duration.fromJSON(object.vtDetentTime)
        : isSet(object.vt_detent_time)
        ? Duration.fromJSON(object.vt_detent_time)
        : undefined,
    };
  },

  toJSON(message: ScvDetentUp): unknown {
    const obj: any = {};
    if (message.scvInputId !== "") {
      obj.scvInputId = message.scvInputId;
    }
    if (message.vtDetentTime !== undefined) {
      obj.vtDetentTime = Duration.toJSON(message.vtDetentTime);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ScvDetentUp>, I>>(base?: I): ScvDetentUp {
    return ScvDetentUp.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ScvDetentUp>, I>>(object: I): ScvDetentUp {
    const message = createBaseScvDetentUp();
    message.scvInputId = object.scvInputId ?? "";
    message.vtDetentTime = (object.vtDetentTime !== undefined && object.vtDetentTime !== null)
      ? Duration.fromPartial(object.vtDetentTime)
      : undefined;
    return message;
  },
};

function createBaseScvDetentDown(): ScvDetentDown {
  return { scvInputId: "", vtDetentTime: undefined };
}

export const ScvDetentDown: MessageFns<ScvDetentDown> = {
  encode(message: ScvDetentDown, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.scvInputId !== "") {
      writer.uint32(10).string(message.scvInputId);
    }
    if (message.vtDetentTime !== undefined) {
      Duration.encode(message.vtDetentTime, writer.uint32(18).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): ScvDetentDown {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseScvDetentDown();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.scvInputId = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.vtDetentTime = Duration.decode(reader, reader.uint32());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ScvDetentDown {
    return {
      scvInputId: isSet(object.scvInputId)
        ? globalThis.String(object.scvInputId)
        : isSet(object.scv_input_id)
        ? globalThis.String(object.scv_input_id)
        : "",
      vtDetentTime: isSet(object.vtDetentTime)
        ? Duration.fromJSON(object.vtDetentTime)
        : isSet(object.vt_detent_time)
        ? Duration.fromJSON(object.vt_detent_time)
        : undefined,
    };
  },

  toJSON(message: ScvDetentDown): unknown {
    const obj: any = {};
    if (message.scvInputId !== "") {
      obj.scvInputId = message.scvInputId;
    }
    if (message.vtDetentTime !== undefined) {
      obj.vtDetentTime = Duration.toJSON(message.vtDetentTime);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ScvDetentDown>, I>>(base?: I): ScvDetentDown {
    return ScvDetentDown.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ScvDetentDown>, I>>(object: I): ScvDetentDown {
    const message = createBaseScvDetentDown();
    message.scvInputId = object.scvInputId ?? "";
    message.vtDetentTime = (object.vtDetentTime !== undefined && object.vtDetentTime !== null)
      ? Duration.fromPartial(object.vtDetentTime)
      : undefined;
    return message;
  },
};

function createBaseScvTimedForce(): ScvTimedForce {
  return { scvInputId: "", forcePct: 0, duration: undefined };
}

export const ScvTimedForce: MessageFns<ScvTimedForce> = {
  encode(message: ScvTimedForce, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.scvInputId !== "") {
      writer.uint32(26).string(message.scvInputId);
    }
    if (message.forcePct !== 0) {
      writer.uint32(8).int32(message.forcePct);
    }
    if (message.duration !== undefined) {
      Duration.encode(message.duration, writer.uint32(18).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): ScvTimedForce {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseScvTimedForce();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 3: {
          if (tag !== 26) {
            break;
          }

          message.scvInputId = reader.string();
          continue;
        }
        case 1: {
          if (tag !== 8) {
            break;
          }

          message.forcePct = reader.int32();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.duration = Duration.decode(reader, reader.uint32());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ScvTimedForce {
    return {
      scvInputId: isSet(object.scvInputId)
        ? globalThis.String(object.scvInputId)
        : isSet(object.scv_input_id)
        ? globalThis.String(object.scv_input_id)
        : "",
      forcePct: isSet(object.forcePct)
        ? globalThis.Number(object.forcePct)
        : isSet(object.force_pct)
        ? globalThis.Number(object.force_pct)
        : 0,
      duration: isSet(object.duration) ? Duration.fromJSON(object.duration) : undefined,
    };
  },

  toJSON(message: ScvTimedForce): unknown {
    const obj: any = {};
    if (message.scvInputId !== "") {
      obj.scvInputId = message.scvInputId;
    }
    if (message.forcePct !== 0) {
      obj.forcePct = Math.round(message.forcePct);
    }
    if (message.duration !== undefined) {
      obj.duration = Duration.toJSON(message.duration);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ScvTimedForce>, I>>(base?: I): ScvTimedForce {
    return ScvTimedForce.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ScvTimedForce>, I>>(object: I): ScvTimedForce {
    const message = createBaseScvTimedForce();
    message.scvInputId = object.scvInputId ?? "";
    message.forcePct = object.forcePct ?? 0;
    message.duration = (object.duration !== undefined && object.duration !== null)
      ? Duration.fromPartial(object.duration)
      : undefined;
    return message;
  },
};

function createBaseScvMoveToPosition(): ScvMoveToPosition {
  return { scvInputId: "", position: 0 };
}

export const ScvMoveToPosition: MessageFns<ScvMoveToPosition> = {
  encode(message: ScvMoveToPosition, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.scvInputId !== "") {
      writer.uint32(18).string(message.scvInputId);
    }
    if (message.position !== 0) {
      writer.uint32(13).float(message.position);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): ScvMoveToPosition {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseScvMoveToPosition();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.scvInputId = reader.string();
          continue;
        }
        case 1: {
          if (tag !== 13) {
            break;
          }

          message.position = reader.float();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ScvMoveToPosition {
    return {
      scvInputId: isSet(object.scvInputId)
        ? globalThis.String(object.scvInputId)
        : isSet(object.scv_input_id)
        ? globalThis.String(object.scv_input_id)
        : "",
      position: isSet(object.position) ? globalThis.Number(object.position) : 0,
    };
  },

  toJSON(message: ScvMoveToPosition): unknown {
    const obj: any = {};
    if (message.scvInputId !== "") {
      obj.scvInputId = message.scvInputId;
    }
    if (message.position !== 0) {
      obj.position = message.position;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ScvMoveToPosition>, I>>(base?: I): ScvMoveToPosition {
    return ScvMoveToPosition.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ScvMoveToPosition>, I>>(object: I): ScvMoveToPosition {
    const message = createBaseScvMoveToPosition();
    message.scvInputId = object.scvInputId ?? "";
    message.position = object.position ?? 0;
    return message;
  },
};

function createBasePtoOn(): PtoOn {
  return { ptoType: 0 };
}

export const PtoOn: MessageFns<PtoOn> = {
  encode(message: PtoOn, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.ptoType !== 0) {
      writer.uint32(8).int32(message.ptoType);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): PtoOn {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePtoOn();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 8) {
            break;
          }

          message.ptoType = reader.int32() as any;
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): PtoOn {
    return {
      ptoType: isSet(object.ptoType)
        ? ptoTypeFromJSON(object.ptoType)
        : isSet(object.pto_type)
        ? ptoTypeFromJSON(object.pto_type)
        : 0,
    };
  },

  toJSON(message: PtoOn): unknown {
    const obj: any = {};
    if (message.ptoType !== 0) {
      obj.ptoType = ptoTypeToJSON(message.ptoType);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<PtoOn>, I>>(base?: I): PtoOn {
    return PtoOn.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<PtoOn>, I>>(object: I): PtoOn {
    const message = createBasePtoOn();
    message.ptoType = object.ptoType ?? 0;
    return message;
  },
};

function createBasePtoOff(): PtoOff {
  return { ptoType: 0 };
}

export const PtoOff: MessageFns<PtoOff> = {
  encode(message: PtoOff, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.ptoType !== 0) {
      writer.uint32(8).int32(message.ptoType);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): PtoOff {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePtoOff();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 8) {
            break;
          }

          message.ptoType = reader.int32() as any;
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): PtoOff {
    return {
      ptoType: isSet(object.ptoType)
        ? ptoTypeFromJSON(object.ptoType)
        : isSet(object.pto_type)
        ? ptoTypeFromJSON(object.pto_type)
        : 0,
    };
  },

  toJSON(message: PtoOff): unknown {
    const obj: any = {};
    if (message.ptoType !== 0) {
      obj.ptoType = ptoTypeToJSON(message.ptoType);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<PtoOff>, I>>(base?: I): PtoOff {
    return PtoOff.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<PtoOff>, I>>(object: I): PtoOff {
    const message = createBasePtoOff();
    message.ptoType = object.ptoType ?? 0;
    return message;
  },
};

function createBaseHitchDetentUp(): HitchDetentUp {
  return { hitchType: 0, calibratedDuration: undefined };
}

export const HitchDetentUp: MessageFns<HitchDetentUp> = {
  encode(message: HitchDetentUp, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.hitchType !== 0) {
      writer.uint32(8).int32(message.hitchType);
    }
    if (message.calibratedDuration !== undefined) {
      Duration.encode(message.calibratedDuration, writer.uint32(18).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): HitchDetentUp {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseHitchDetentUp();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 8) {
            break;
          }

          message.hitchType = reader.int32() as any;
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.calibratedDuration = Duration.decode(reader, reader.uint32());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): HitchDetentUp {
    return {
      hitchType: isSet(object.hitchType)
        ? hitchTypeFromJSON(object.hitchType)
        : isSet(object.hitch_type)
        ? hitchTypeFromJSON(object.hitch_type)
        : 0,
      calibratedDuration: isSet(object.calibratedDuration)
        ? Duration.fromJSON(object.calibratedDuration)
        : isSet(object.calibrated_duration)
        ? Duration.fromJSON(object.calibrated_duration)
        : undefined,
    };
  },

  toJSON(message: HitchDetentUp): unknown {
    const obj: any = {};
    if (message.hitchType !== 0) {
      obj.hitchType = hitchTypeToJSON(message.hitchType);
    }
    if (message.calibratedDuration !== undefined) {
      obj.calibratedDuration = Duration.toJSON(message.calibratedDuration);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<HitchDetentUp>, I>>(base?: I): HitchDetentUp {
    return HitchDetentUp.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<HitchDetentUp>, I>>(object: I): HitchDetentUp {
    const message = createBaseHitchDetentUp();
    message.hitchType = object.hitchType ?? 0;
    message.calibratedDuration = (object.calibratedDuration !== undefined && object.calibratedDuration !== null)
      ? Duration.fromPartial(object.calibratedDuration)
      : undefined;
    return message;
  },
};

function createBaseHitchDetentDown(): HitchDetentDown {
  return { hitchType: 0, calibratedDuration: undefined };
}

export const HitchDetentDown: MessageFns<HitchDetentDown> = {
  encode(message: HitchDetentDown, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.hitchType !== 0) {
      writer.uint32(8).int32(message.hitchType);
    }
    if (message.calibratedDuration !== undefined) {
      Duration.encode(message.calibratedDuration, writer.uint32(18).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): HitchDetentDown {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseHitchDetentDown();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 8) {
            break;
          }

          message.hitchType = reader.int32() as any;
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.calibratedDuration = Duration.decode(reader, reader.uint32());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): HitchDetentDown {
    return {
      hitchType: isSet(object.hitchType)
        ? hitchTypeFromJSON(object.hitchType)
        : isSet(object.hitch_type)
        ? hitchTypeFromJSON(object.hitch_type)
        : 0,
      calibratedDuration: isSet(object.calibratedDuration)
        ? Duration.fromJSON(object.calibratedDuration)
        : isSet(object.calibrated_duration)
        ? Duration.fromJSON(object.calibrated_duration)
        : undefined,
    };
  },

  toJSON(message: HitchDetentDown): unknown {
    const obj: any = {};
    if (message.hitchType !== 0) {
      obj.hitchType = hitchTypeToJSON(message.hitchType);
    }
    if (message.calibratedDuration !== undefined) {
      obj.calibratedDuration = Duration.toJSON(message.calibratedDuration);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<HitchDetentDown>, I>>(base?: I): HitchDetentDown {
    return HitchDetentDown.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<HitchDetentDown>, I>>(object: I): HitchDetentDown {
    const message = createBaseHitchDetentDown();
    message.hitchType = object.hitchType ?? 0;
    message.calibratedDuration = (object.calibratedDuration !== undefined && object.calibratedDuration !== null)
      ? Duration.fromPartial(object.calibratedDuration)
      : undefined;
    return message;
  },
};

function createBaseHitchTimedForce(): HitchTimedForce {
  return { hitchType: 0, forcePct: 0, duration: undefined };
}

export const HitchTimedForce: MessageFns<HitchTimedForce> = {
  encode(message: HitchTimedForce, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.hitchType !== 0) {
      writer.uint32(24).int32(message.hitchType);
    }
    if (message.forcePct !== 0) {
      writer.uint32(8).int32(message.forcePct);
    }
    if (message.duration !== undefined) {
      Duration.encode(message.duration, writer.uint32(18).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): HitchTimedForce {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseHitchTimedForce();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 3: {
          if (tag !== 24) {
            break;
          }

          message.hitchType = reader.int32() as any;
          continue;
        }
        case 1: {
          if (tag !== 8) {
            break;
          }

          message.forcePct = reader.int32();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.duration = Duration.decode(reader, reader.uint32());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): HitchTimedForce {
    return {
      hitchType: isSet(object.hitchType)
        ? hitchTypeFromJSON(object.hitchType)
        : isSet(object.hitch_type)
        ? hitchTypeFromJSON(object.hitch_type)
        : 0,
      forcePct: isSet(object.forcePct)
        ? globalThis.Number(object.forcePct)
        : isSet(object.force_pct)
        ? globalThis.Number(object.force_pct)
        : 0,
      duration: isSet(object.duration) ? Duration.fromJSON(object.duration) : undefined,
    };
  },

  toJSON(message: HitchTimedForce): unknown {
    const obj: any = {};
    if (message.hitchType !== 0) {
      obj.hitchType = hitchTypeToJSON(message.hitchType);
    }
    if (message.forcePct !== 0) {
      obj.forcePct = Math.round(message.forcePct);
    }
    if (message.duration !== undefined) {
      obj.duration = Duration.toJSON(message.duration);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<HitchTimedForce>, I>>(base?: I): HitchTimedForce {
    return HitchTimedForce.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<HitchTimedForce>, I>>(object: I): HitchTimedForce {
    const message = createBaseHitchTimedForce();
    message.hitchType = object.hitchType ?? 0;
    message.forcePct = object.forcePct ?? 0;
    message.duration = (object.duration !== undefined && object.duration !== null)
      ? Duration.fromPartial(object.duration)
      : undefined;
    return message;
  },
};

function createBaseLaserWeederEstopOn(): LaserWeederEstopOn {
  return { implementId: "" };
}

export const LaserWeederEstopOn: MessageFns<LaserWeederEstopOn> = {
  encode(message: LaserWeederEstopOn, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.implementId !== "") {
      writer.uint32(10).string(message.implementId);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): LaserWeederEstopOn {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseLaserWeederEstopOn();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.implementId = reader.string();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): LaserWeederEstopOn {
    return {
      implementId: isSet(object.implementId)
        ? globalThis.String(object.implementId)
        : isSet(object.implement_id)
        ? globalThis.String(object.implement_id)
        : "",
    };
  },

  toJSON(message: LaserWeederEstopOn): unknown {
    const obj: any = {};
    if (message.implementId !== "") {
      obj.implementId = message.implementId;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<LaserWeederEstopOn>, I>>(base?: I): LaserWeederEstopOn {
    return LaserWeederEstopOn.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<LaserWeederEstopOn>, I>>(object: I): LaserWeederEstopOn {
    const message = createBaseLaserWeederEstopOn();
    message.implementId = object.implementId ?? "";
    return message;
  },
};

function createBaseLaserWeederEstopOff(): LaserWeederEstopOff {
  return { implementId: "" };
}

export const LaserWeederEstopOff: MessageFns<LaserWeederEstopOff> = {
  encode(message: LaserWeederEstopOff, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.implementId !== "") {
      writer.uint32(10).string(message.implementId);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): LaserWeederEstopOff {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseLaserWeederEstopOff();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.implementId = reader.string();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): LaserWeederEstopOff {
    return {
      implementId: isSet(object.implementId)
        ? globalThis.String(object.implementId)
        : isSet(object.implement_id)
        ? globalThis.String(object.implement_id)
        : "",
    };
  },

  toJSON(message: LaserWeederEstopOff): unknown {
    const obj: any = {};
    if (message.implementId !== "") {
      obj.implementId = message.implementId;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<LaserWeederEstopOff>, I>>(base?: I): LaserWeederEstopOff {
    return LaserWeederEstopOff.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<LaserWeederEstopOff>, I>>(object: I): LaserWeederEstopOff {
    const message = createBaseLaserWeederEstopOff();
    message.implementId = object.implementId ?? "";
    return message;
  },
};

function createBaseWait(): Wait {
  return { duration: undefined };
}

export const Wait: MessageFns<Wait> = {
  encode(message: Wait, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.duration !== undefined) {
      Duration.encode(message.duration, writer.uint32(10).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): Wait {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseWait();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.duration = Duration.decode(reader, reader.uint32());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): Wait {
    return { duration: isSet(object.duration) ? Duration.fromJSON(object.duration) : undefined };
  },

  toJSON(message: Wait): unknown {
    const obj: any = {};
    if (message.duration !== undefined) {
      obj.duration = Duration.toJSON(message.duration);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<Wait>, I>>(base?: I): Wait {
    return Wait.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<Wait>, I>>(object: I): Wait {
    const message = createBaseWait();
    message.duration = (object.duration !== undefined && object.duration !== null)
      ? Duration.fromPartial(object.duration)
      : undefined;
    return message;
  },
};

function createBaseSetEngineRpmToMax(): SetEngineRpmToMax {
  return {};
}

export const SetEngineRpmToMax: MessageFns<SetEngineRpmToMax> = {
  encode(_: SetEngineRpmToMax, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): SetEngineRpmToMax {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSetEngineRpmToMax();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(_: any): SetEngineRpmToMax {
    return {};
  },

  toJSON(_: SetEngineRpmToMax): unknown {
    const obj: any = {};
    return obj;
  },

  create<I extends Exact<DeepPartial<SetEngineRpmToMax>, I>>(base?: I): SetEngineRpmToMax {
    return SetEngineRpmToMax.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SetEngineRpmToMax>, I>>(_: I): SetEngineRpmToMax {
    const message = createBaseSetEngineRpmToMax();
    return message;
  },
};

function createBaseSetEngineRpmToMin(): SetEngineRpmToMin {
  return {};
}

export const SetEngineRpmToMin: MessageFns<SetEngineRpmToMin> = {
  encode(_: SetEngineRpmToMin, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): SetEngineRpmToMin {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSetEngineRpmToMin();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(_: any): SetEngineRpmToMin {
    return {};
  },

  toJSON(_: SetEngineRpmToMin): unknown {
    const obj: any = {};
    return obj;
  },

  create<I extends Exact<DeepPartial<SetEngineRpmToMin>, I>>(base?: I): SetEngineRpmToMin {
    return SetEngineRpmToMin.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SetEngineRpmToMin>, I>>(_: I): SetEngineRpmToMin {
    const message = createBaseSetEngineRpmToMin();
    return message;
  },
};

function createBaseAdvancedAction(): AdvancedAction {
  return { actionName: "", intParam: 0, floatParam: 0 };
}

export const AdvancedAction: MessageFns<AdvancedAction> = {
  encode(message: AdvancedAction, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.actionName !== "") {
      writer.uint32(10).string(message.actionName);
    }
    if (message.intParam !== 0) {
      writer.uint32(16).int32(message.intParam);
    }
    if (message.floatParam !== 0) {
      writer.uint32(29).float(message.floatParam);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): AdvancedAction {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseAdvancedAction();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.actionName = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 16) {
            break;
          }

          message.intParam = reader.int32();
          continue;
        }
        case 3: {
          if (tag !== 29) {
            break;
          }

          message.floatParam = reader.float();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): AdvancedAction {
    return {
      actionName: isSet(object.actionName)
        ? globalThis.String(object.actionName)
        : isSet(object.action_name)
        ? globalThis.String(object.action_name)
        : "",
      intParam: isSet(object.intParam)
        ? globalThis.Number(object.intParam)
        : isSet(object.int_param)
        ? globalThis.Number(object.int_param)
        : 0,
      floatParam: isSet(object.floatParam)
        ? globalThis.Number(object.floatParam)
        : isSet(object.float_param)
        ? globalThis.Number(object.float_param)
        : 0,
    };
  },

  toJSON(message: AdvancedAction): unknown {
    const obj: any = {};
    if (message.actionName !== "") {
      obj.actionName = message.actionName;
    }
    if (message.intParam !== 0) {
      obj.intParam = Math.round(message.intParam);
    }
    if (message.floatParam !== 0) {
      obj.floatParam = message.floatParam;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<AdvancedAction>, I>>(base?: I): AdvancedAction {
    return AdvancedAction.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<AdvancedAction>, I>>(object: I): AdvancedAction {
    const message = createBaseAdvancedAction();
    message.actionName = object.actionName ?? "";
    message.intParam = object.intParam ?? 0;
    message.floatParam = object.floatParam ?? 0;
    return message;
  },
};

function createBaseScvConstantForce(): ScvConstantForce {
  return { scvInputId: "", forcePct: 0 };
}

export const ScvConstantForce: MessageFns<ScvConstantForce> = {
  encode(message: ScvConstantForce, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.scvInputId !== "") {
      writer.uint32(18).string(message.scvInputId);
    }
    if (message.forcePct !== 0) {
      writer.uint32(8).int32(message.forcePct);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): ScvConstantForce {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseScvConstantForce();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.scvInputId = reader.string();
          continue;
        }
        case 1: {
          if (tag !== 8) {
            break;
          }

          message.forcePct = reader.int32();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ScvConstantForce {
    return {
      scvInputId: isSet(object.scvInputId)
        ? globalThis.String(object.scvInputId)
        : isSet(object.scv_input_id)
        ? globalThis.String(object.scv_input_id)
        : "",
      forcePct: isSet(object.forcePct)
        ? globalThis.Number(object.forcePct)
        : isSet(object.force_pct)
        ? globalThis.Number(object.force_pct)
        : 0,
    };
  },

  toJSON(message: ScvConstantForce): unknown {
    const obj: any = {};
    if (message.scvInputId !== "") {
      obj.scvInputId = message.scvInputId;
    }
    if (message.forcePct !== 0) {
      obj.forcePct = Math.round(message.forcePct);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ScvConstantForce>, I>>(base?: I): ScvConstantForce {
    return ScvConstantForce.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ScvConstantForce>, I>>(object: I): ScvConstantForce {
    const message = createBaseScvConstantForce();
    message.scvInputId = object.scvInputId ?? "";
    message.forcePct = object.forcePct ?? 0;
    return message;
  },
};

function createBaseScvHoldPosition(): ScvHoldPosition {
  return { scvInputId: "", position: 0 };
}

export const ScvHoldPosition: MessageFns<ScvHoldPosition> = {
  encode(message: ScvHoldPosition, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.scvInputId !== "") {
      writer.uint32(18).string(message.scvInputId);
    }
    if (message.position !== 0) {
      writer.uint32(13).float(message.position);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): ScvHoldPosition {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseScvHoldPosition();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.scvInputId = reader.string();
          continue;
        }
        case 1: {
          if (tag !== 13) {
            break;
          }

          message.position = reader.float();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ScvHoldPosition {
    return {
      scvInputId: isSet(object.scvInputId)
        ? globalThis.String(object.scvInputId)
        : isSet(object.scv_input_id)
        ? globalThis.String(object.scv_input_id)
        : "",
      position: isSet(object.position) ? globalThis.Number(object.position) : 0,
    };
  },

  toJSON(message: ScvHoldPosition): unknown {
    const obj: any = {};
    if (message.scvInputId !== "") {
      obj.scvInputId = message.scvInputId;
    }
    if (message.position !== 0) {
      obj.position = message.position;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ScvHoldPosition>, I>>(base?: I): ScvHoldPosition {
    return ScvHoldPosition.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ScvHoldPosition>, I>>(object: I): ScvHoldPosition {
    const message = createBaseScvHoldPosition();
    message.scvInputId = object.scvInputId ?? "";
    message.position = object.position ?? 0;
    return message;
  },
};

function createBaseScvFloat(): ScvFloat {
  return { scvInputId: "" };
}

export const ScvFloat: MessageFns<ScvFloat> = {
  encode(message: ScvFloat, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.scvInputId !== "") {
      writer.uint32(10).string(message.scvInputId);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): ScvFloat {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseScvFloat();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.scvInputId = reader.string();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ScvFloat {
    return {
      scvInputId: isSet(object.scvInputId)
        ? globalThis.String(object.scvInputId)
        : isSet(object.scv_input_id)
        ? globalThis.String(object.scv_input_id)
        : "",
    };
  },

  toJSON(message: ScvFloat): unknown {
    const obj: any = {};
    if (message.scvInputId !== "") {
      obj.scvInputId = message.scvInputId;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ScvFloat>, I>>(base?: I): ScvFloat {
    return ScvFloat.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ScvFloat>, I>>(object: I): ScvFloat {
    const message = createBaseScvFloat();
    message.scvInputId = object.scvInputId ?? "";
    return message;
  },
};

function createBaseHitchHoldPosition(): HitchHoldPosition {
  return { hitchType: 0, position: 0 };
}

export const HitchHoldPosition: MessageFns<HitchHoldPosition> = {
  encode(message: HitchHoldPosition, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.hitchType !== 0) {
      writer.uint32(16).int32(message.hitchType);
    }
    if (message.position !== 0) {
      writer.uint32(13).float(message.position);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): HitchHoldPosition {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseHitchHoldPosition();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 2: {
          if (tag !== 16) {
            break;
          }

          message.hitchType = reader.int32() as any;
          continue;
        }
        case 1: {
          if (tag !== 13) {
            break;
          }

          message.position = reader.float();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): HitchHoldPosition {
    return {
      hitchType: isSet(object.hitchType)
        ? hitchTypeFromJSON(object.hitchType)
        : isSet(object.hitch_type)
        ? hitchTypeFromJSON(object.hitch_type)
        : 0,
      position: isSet(object.position) ? globalThis.Number(object.position) : 0,
    };
  },

  toJSON(message: HitchHoldPosition): unknown {
    const obj: any = {};
    if (message.hitchType !== 0) {
      obj.hitchType = hitchTypeToJSON(message.hitchType);
    }
    if (message.position !== 0) {
      obj.position = message.position;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<HitchHoldPosition>, I>>(base?: I): HitchHoldPosition {
    return HitchHoldPosition.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<HitchHoldPosition>, I>>(object: I): HitchHoldPosition {
    const message = createBaseHitchHoldPosition();
    message.hitchType = object.hitchType ?? 0;
    message.position = object.position ?? 0;
    return message;
  },
};

function createBaseHitchFloat(): HitchFloat {
  return { hitchType: 0 };
}

export const HitchFloat: MessageFns<HitchFloat> = {
  encode(message: HitchFloat, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.hitchType !== 0) {
      writer.uint32(8).int32(message.hitchType);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): HitchFloat {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseHitchFloat();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 8) {
            break;
          }

          message.hitchType = reader.int32() as any;
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): HitchFloat {
    return {
      hitchType: isSet(object.hitchType)
        ? hitchTypeFromJSON(object.hitchType)
        : isSet(object.hitch_type)
        ? hitchTypeFromJSON(object.hitch_type)
        : 0,
    };
  },

  toJSON(message: HitchFloat): unknown {
    const obj: any = {};
    if (message.hitchType !== 0) {
      obj.hitchType = hitchTypeToJSON(message.hitchType);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<HitchFloat>, I>>(base?: I): HitchFloat {
    return HitchFloat.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<HitchFloat>, I>>(object: I): HitchFloat {
    const message = createBaseHitchFloat();
    message.hitchType = object.hitchType ?? 0;
    return message;
  },
};

function createBaseLaserweederOn(): LaserweederOn {
  return { implementId: "" };
}

export const LaserweederOn: MessageFns<LaserweederOn> = {
  encode(message: LaserweederOn, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.implementId !== "") {
      writer.uint32(10).string(message.implementId);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): LaserweederOn {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseLaserweederOn();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.implementId = reader.string();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): LaserweederOn {
    return {
      implementId: isSet(object.implementId)
        ? globalThis.String(object.implementId)
        : isSet(object.implement_id)
        ? globalThis.String(object.implement_id)
        : "",
    };
  },

  toJSON(message: LaserweederOn): unknown {
    const obj: any = {};
    if (message.implementId !== "") {
      obj.implementId = message.implementId;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<LaserweederOn>, I>>(base?: I): LaserweederOn {
    return LaserweederOn.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<LaserweederOn>, I>>(object: I): LaserweederOn {
    const message = createBaseLaserweederOn();
    message.implementId = object.implementId ?? "";
    return message;
  },
};

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}

export interface MessageFns<T> {
  encode(message: T, writer?: BinaryWriter): BinaryWriter;
  decode(input: BinaryReader | Uint8Array, length?: number): T;
  fromJSON(object: any): T;
  toJSON(message: T): unknown;
  create<I extends Exact<DeepPartial<T>, I>>(base?: I): T;
  fromPartial<I extends Exact<DeepPartial<T>, I>>(object: I): T;
}
